/**
 * MobileAppPage.jsx
 * Admin page for mobile app management with live preview
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, QrCode, Copy, Printer, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import MobileAppManagement from '@/components/admin/MobileAppManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JoinQrCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/admin/mobile-app/join-qr`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('QR-Code konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [token]);

  const copyLink = () => {
    navigator.clipboard?.writeText(data.deep_link);
    toast.success('Link kopiert');
  };

  const printQr = () => {
    const w = window.open('', '_blank');
    if (!w || !data) return;
    w.document.write(`<html><head><title>${data.name} — App beitreten</title></head>
      <body style="font-family:Arial;text-align:center;padding:48px">
        <h2>${data.name}</h2>
        <p>Scanne den Code, um dich in der App zu verbinden</p>
        <img src="${data.qr_png}" style="width:320px;height:320px"/>
        <p style="color:#555">${data.deep_link}</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="join-qr-card">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
        <QrCode className="h-4 w-4" /> App beitreten — QR-Code &amp; Link
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Hänge diesen QR-Code im Betrieb aus. Endkunden scannen ihn und verbinden sich in der App direkt mit deinem Betrieb.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Lade…</div>
      ) : data ? (
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <img src={data.qr_png} alt="Beitritts-QR" className="h-40 w-40 rounded-lg border border-slate-200 p-2" data-testid="join-qr-img" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="text-xs text-slate-500">Beitritts-Link</div>
            <div className="truncate rounded-lg bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700" data-testid="join-qr-link">{data.deep_link}</div>
            <div className="flex gap-2 pt-1">
              <button onClick={copyLink} data-testid="join-qr-copy" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                <Copy className="h-3.5 w-3.5" /> Link kopieren
              </button>
              <button onClick={printQr} data-testid="join-qr-print" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                <Printer className="h-3.5 w-3.5" /> Drucken
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const MobileAppPage = ({ embedded = false }) => {
  const { token } = useAuth();

  return (
    <div className="p-6">
      {!embedded && (
        <a href="/settings" data-testid="mobile-app-back-btn" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Zurück zu Einstellungen
        </a>
      )}
      <JoinQrCard token={token} />
      <MobileAppManagement token={token} />
    </div>
  );
};

export default MobileAppPage;
